import React from 'react';
import { Metro, State, Store } from 'clinics/clinics';
import { S35ClinicInfo } from '@milanlaser/mlcomponents';
import { type PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { getLocationContext } from '../../../../utils/general/getLocationContext';
import { processClinicImagesForCarousel } from '../../../../utils/general/clinicImageUtils';
import { ReviewsData } from '../../../../utils/ssr/types';
import { useOptimizedImage } from '../../../../hooks/useOptimizedImage';
import { createImageRenderer } from '../../../../utils/general/renderOptimizedImage';
import { storeHours, StoreHoursEntry } from '../../../../utils/store-hours';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S35ClinicInfoSectionProps = BuilderProps<{
    metro: Metro;
    state: State;
    store: Store;
    clinicImages: Array<{
        name: string;
        downloadUrl: string;
    }>;
    reviewsData?: ReviewsData;
    optimizedMainImageNodeId?: string;
}> & {
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    mapIcon: string;
    phoneIcon: string;
    clockIcon: string;
};

export function S35ClinicInfoSection(props: S35ClinicInfoSectionProps) {
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        mapIcon,
        phoneIcon,
        clockIcon,
        ...restProps
    } = props;

    const { builderState } = restProps;
    const { state } = builderState;

    const hasState = state && state?.store && state?.metro;
    if (!hasState) {
        return <h2 className="py-4">Oops! This component can only be used on clinic pages.</h2>;
    }

    const locationName = getLocationContext({
        state: {
            ...builderState.state,
            model: 'clinic-page',
        },
    });
    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const customAltText = state.store.clinic_local_image_alt;
    const finalCarouselItems = processClinicImagesForCarousel(
        state.clinicImages,
        locationName,
        undefined,
        customAltText
    );

    const heroAlt = customAltText || `${locationName} clinic exterior`;
    const { gatsbyImageData } = useOptimizedImage(state.optimizedMainImageNodeId || '', heroAlt, 'FULL_WIDTH');

    const firstImageUrl = finalCarouselItems[0]?.image || '';

    const renderCarouselImage = createImageRenderer({
        optimizedImageData: gatsbyImageData,
        optimizedAlt: heroAlt,
        shouldUseOptimized: (src) => src === firstImageUrl && !!gatsbyImageData,
    });

    const reviews = state?.reviewsData
        ? {
              value: state.reviewsData.average || 0,
              reviewCount: state.reviewsData.total || 0,
          }
        : undefined;

    const displayCity = state.store.marketingCity || state.metro.name;
    const metroName = state.metro.name;
    const clinicH1Title = displayCity.toLowerCase().includes(metroName.toLowerCase())
        ? `Best Laser Hair Removal in ${displayCity}`
        : `Best Laser Hair Removal in ${displayCity} | ${metroName} Area`;

    const clinicDirections = state?.store?.clinic_directions || '';

    return (
        <section className={`${topPaddingClasses} ${bottomPaddingClasses}`}>
            <div className="container">
                <S35ClinicInfo
                    carouselProps={{
                        items: finalCarouselItems,
                    }}
                    clinicInfoProps={{
                        clockImage: clockIcon,
                        clockImageAlt: 'Clock icon',
                        phoneImage: phoneIcon,
                        phoneImageAlt: 'Phone icon',
                        mapPinImage: mapIcon,
                        mapPinImageAlt: 'Map icon',
                        title: clinicH1Title,
                        address: {
                            line1: state.store.address,
                            line2: '',
                            city: state.store.addressCity || state.store.marketingCity,
                            state: state.store.stateAbbrev.toUpperCase(),
                            zip: state.store.zipCode,
                        },
                        rating: reviews,
                        phone: state.store.phone,
                        isOpen: isStoreOpen(),
                        closesAt: getClosingTimeDisplay(),
                        hours: storeHours,
                        servicesUrl: '/what-is-laser-hair-removal',
                        bookNowUrl: '#consult',
                        directions: clinicDirections,
                    }}
                    renderImage={renderCarouselImage}
                />
            </div>
        </section>
    );
}

const dayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};

function expandDays(range: string): number[] {
    if (range.includes('-')) {
        const [start, end] = range.split('-');
        const startIdx = dayMap[start];
        const endIdx = dayMap[end];
        if (startIdx <= endIdx) return Array.from({ length: endIdx - startIdx + 1 }, (_, i) => startIdx + i);
        return [
            ...Array.from({ length: 7 - startIdx }, (_, i) => startIdx + i),
            ...Array.from({ length: endIdx + 1 }, (_, i) => i),
        ];
    }
    return [dayMap[range]];
}

export function to24HourTime(timeStr: string): [number, number] {
    // 1) Trim off any surrounding whitespace
    let str = timeStr.trim();

    // 2) Separate out an AM/PM suffix if present
    let ampm = '';
    const parts = str.split(' ');
    if (parts.length === 2) {
        [str, ampm] = [parts[0], parts[1].toUpperCase()];
    }

    // 3) Split hours and minutes (if no ":", minutes = 0)
    const [hourPart, minutePart] = str.split(':');
    let hour = parseInt(hourPart, 10);
    const minute = minutePart ? parseInt(minutePart, 10) : 0;

    // 4) Adjust for AM/PM
    if (ampm === 'AM' && hour === 12) {
        hour = 0; // 12 AM → 0:00
    } else if (ampm === 'PM' && hour < 12) {
        hour += 12; // 1 PM→13, … 11 PM→23
    }

    return [hour, minute];
}

export function buildDailyHoursMap(config: StoreHoursEntry[]) {
    const map: Record<number, { open: string; close: string } | null> = {};

    config.forEach(({ days, hours }) => {
        const dayParts = days.split(',').flatMap((part) => expandDays(part.trim()));
        const isClosed = hours.toLowerCase() === 'closed';
        dayParts.forEach((dayIndex) => {
            if (isClosed) {
                map[dayIndex] = null;
            } else {
                const [open, close] = hours.split(' - ');
                map[dayIndex] = { open, close };
            }
        });
    });

    return map;
}

const dailyHours = buildDailyHoursMap(storeHours);

export function isStoreOpen() {
    const now = new Date();
    const day = now.getDay(); // 0 (Sun) – 6 (Sat)
    const schedule = dailyHours[day];

    if (!schedule) return false;

    const [openH, openM] = to24HourTime(schedule.open);
    const [closeH, closeM] = to24HourTime(schedule.close);

    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;

    return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
}

export function getClosingTimeDisplay() {
    const now = new Date();
    const day = now.getDay(); // 0–6
    const schedule = dailyHours[day];

    if (!schedule) return 'Closed';

    const [closeH, closeM] = to24HourTime(schedule.close);
    const closeDate = new Date();
    closeDate.setHours(closeH, closeM, 0);

    return `Closes at ${closeDate
        .toLocaleTimeString([], {
            hour: 'numeric',
            hour12: true,
        })
        .toUpperCase()}`;
}
